import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { MainLayout } from './layouts/MainLayout';

// Context & Protection
import { AuthProvider } from './contexts/AuthContext';
import { UnreadProvider } from './contexts/UnreadContext';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy-loaded pages — split into per-route chunks
const MinistryPage = lazy(() => import('./pages/MinistryPage').then(m => ({ default: m.MinistryPage })));
const MinistryListPage = lazy(() => import('./pages/MinistryListPage').then(m => ({ default: m.MinistryListPage })));
const PressReleasePage = lazy(() => import('./pages/PressReleasePage').then(m => ({ default: m.PressReleasePage })));
const DisclaimerPage = lazy(() => import('./pages/DisclaimerPage').then(m => ({ default: m.DisclaimerPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));
const SettingsPage = lazy(() => import('./pages/my-dash/settings/SettingsPage'));
const SubmitPage = lazy(() => import('./pages/my-dash/SubmitPage'));
const LibraryPage = lazy(() => import('./pages/my-dash/LibraryPage'));
const JobStatusPage = lazy(() => import('./pages/my-dash/JobStatusPage'));
const SummaryDetailPage = lazy(() => import('./pages/my-dash/SummaryDetailPage'));
const XAccountsPage = lazy(() => import('./pages/my-dash/XAccountsPage'));
const YouTubeChannelsPage = lazy(() => import('./pages/my-dash/YouTubeChannelsPage'));
const CorporatesPage = lazy(() => import('./pages/my-dash/CorporatesPage'));
const UsagePage = lazy(() => import('./pages/my-dash/UsagePage'));
const DocumentsPage = lazy(() => import('./pages/my-dash/DocumentsPage'));

function App() {
  return (
    <SWRConfig value={{ focusThrottleInterval: 5 * 60 * 1000 }}>

      <AuthProvider>
        <UnreadProvider>
          <MainLayout>
          <Suspense>
          <Routes>
            {/* All content routes require login */}
            <Route path="/" element={<Navigate to="/my-dash/library" replace />} />
            <Route path="/ministry" element={<ProtectedRoute><MinistryListPage /></ProtectedRoute>} />
            <Route path="/ministry/:id" element={<ProtectedRoute><MinistryPage /></ProtectedRoute>} />
            <Route path="/press" element={<Navigate to="/press/pmo" replace />} />
            <Route path="/press/:slug" element={<ProtectedRoute><PressReleasePage /></ProtectedRoute>} />
            <Route path="/disclaimer" element={<ProtectedRoute><DisclaimerPage /></ProtectedRoute>} />
            <Route path="/about" element={<ProtectedRoute><AboutPage /></ProtectedRoute>} />
            
            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/my-dash" element={<Navigate to="/my-dash/library" replace />} />
            
            {/* Protected My Dash Routes */}
            <Route path="/my-dash/submit" element={
              <ProtectedRoute>
                <SubmitPage />
              </ProtectedRoute>
            } />
            <Route path="/my-dash/library" element={
              <ProtectedRoute>
                <LibraryPage />
              </ProtectedRoute>
            } />
            <Route path="/my-dash/settings" element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/my-dash/jobs/:id" element={
              <ProtectedRoute>
                <JobStatusPage />
              </ProtectedRoute>
            } />
            <Route path="/my-dash/summaries/:id" element={
              <ProtectedRoute>
                <SummaryDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/my-dash/x-accounts" element={
              <ProtectedRoute>
                <XAccountsPage />
              </ProtectedRoute>
            } />
            <Route path="/my-dash/yt-channels" element={
              <ProtectedRoute>
                <YouTubeChannelsPage />
              </ProtectedRoute>
            } />
            <Route path="/my-dash/usage" element={
              <ProtectedRoute>
                <UsagePage />
              </ProtectedRoute>
            } />
            <Route path="/my-dash/corporates" element={
              <ProtectedRoute>
                <CorporatesPage />
              </ProtectedRoute>
            } />
            {/* Documents Section */}
            <Route path="/documents/files" element={
              <ProtectedRoute>
                <DocumentsPage />
              </ProtectedRoute>
            } />
            <Route path="/documents/submit" element={
              <ProtectedRoute>
                <SubmitPage fileOnly />
              </ProtectedRoute>
            } />
            <Route path="/documents" element={<Navigate to="/documents/files" replace />} />
            <Route path="/my-dash/documents" element={<Navigate to="/documents/files" replace />} />
          </Routes>
          </Suspense>
        </MainLayout>
        </UnreadProvider>
      </AuthProvider>
    </SWRConfig>
  );
}

export default App;